import type { PitchFrame } from '@tunasong/music-lib'
import { detectPitch, pitchHopSize, pitchWindowSize } from './processors/pitch.js'
import { rms } from './utilities.js'

export interface PitchFrameSourceOptions {
  sampleRate: number
  windowSize?: number
  hopSize?: number
}

/**
 * Turns the AudioWorklet's 128-sample blocks into overlapping analysis frames.
 *
 * Deliberately separate from the worklet shell: the buffer arithmetic and the frame timestamps
 * are the part most likely to be wrong, and they are the part a real browser cannot tell you
 * anything useful about. The worklet itself is then thin enough to read.
 *
 * The window is a ring buffer, unwrapped into a scratch array once per hop rather than shifted
 * once per sample — on the audio thread the difference is between two copies per sample and
 * two thousand.
 */
export const createPitchFrameSource = (options: PitchFrameSourceOptions) => {
  const { sampleRate, windowSize = pitchWindowSize, hopSize = pitchHopSize } = options

  const ring = new Float32Array(windowSize)
  const scratch = new Float32Array(windowSize)
  /** Where the next sample is written; also the oldest sample in the ring once it has wrapped. */
  let writeIndex = 0
  /** Samples written since the last emitted frame. */
  let filled = 0
  /** Total samples consumed, so frame times survive the ring wrapping under them. */
  let consumed = 0
  let primed = false

  const emit = (): PitchFrame => {
    for (let i = 0; i < windowSize; i++) {
      scratch[i] = ring[(writeIndex + i) % windowSize]
    }
    const detected = detectPitch(scratch, sampleRate)
    return {
      /** The centre of the window is the least biased estimate of when this pitch was sung. */
      time: (consumed - windowSize / 2) / sampleRate,
      frequency: detected?.frequency ?? 0,
      clarity: detected?.clarity ?? 0,
      rms: rms(scratch),
    }
  }

  return {
    push: (block: Float32Array): PitchFrame[] => {
      const frames: PitchFrame[] = []
      for (const sample of block) {
        ring[writeIndex] = sample
        writeIndex = (writeIndex + 1) % windowSize
        consumed++
        filled++

        if (!primed) {
          if (consumed < windowSize) {
            continue
          }
          primed = true
          filled = 0
          frames.push(emit())
          continue
        }
        if (filled >= hopSize) {
          filled = 0
          frames.push(emit())
        }
      }
      return frames
    },
  }
}
